import { useState, useEffect } from 'react';
import { hotelService } from '../../services/api';

const HotelList = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [hotelStats, setHotelStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    country: '',
    timezone: '',
    phone: '',
    email: '',
    is_active: true,
  });

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      const response = await hotelService.getAll();
      setHotels(response.data.data);
    } catch (error) {
      console.error('Failed to fetch hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (hotel) => {
    setSelectedHotel(hotel);
    setFormData({
      name: hotel.name || '',
      code: hotel.code || '',
      address: hotel.address || '',
      city: hotel.city || '',
      country: hotel.country || '',
      timezone: hotel.timezone || '',
      phone: hotel.phone || '',
      email: hotel.email || '',
      is_active: hotel.is_active !== false,
    });
    setShowEditModal(true);
  };

  const handleViewStats = async (hotel) => {
    setSelectedHotel(hotel);
    setShowStatsModal(true);
    setStatsLoading(true);
    try {
      const response = await hotelService.getStats(hotel.id);
      setHotelStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch hotel stats:', error);
      setHotelStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAddHotel = () => {
    setSelectedHotel(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      city: '',
      country: '',
      timezone: 'Asia/Singapore',
      phone: '',
      email: '',
      is_active: true,
    });
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedHotel) {
        await hotelService.update(selectedHotel.id, formData);
      } else {
        await hotelService.create(formData);
      }
      setShowEditModal(false);
      setShowAddModal(false);
      fetchHotels();
    } catch (error) {
      console.error('Failed to save hotel:', error);
      alert(error.response?.data?.message || 'Failed to save hotel');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowStatsModal(false);
    setShowAddModal(false);
    setSelectedHotel(null);
    setHotelStats(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hotels</h1>
          <p className="text-gray-600">Manage hotel properties across the network</p>
        </div>
        <button onClick={handleAddHotel} className="btn-primary">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Hotel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <div className="spinner h-8 w-8"></div>
          </div>
        ) : hotels.length > 0 ? (
          hotels.map((hotel) => (
            <div key={hotel.id} className="card hover:shadow-lg transition-shadow">
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{hotel.name}</h3>
                    <span className="badge badge-blue mt-1">{hotel.code}</span>
                  </div>
                  <span className={`badge ${hotel.is_active ? 'badge-green' : 'badge-red'}`}>
                    {hotel.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {hotel.city}, {hotel.country}
                  </div>
                  {hotel.phone && (
                    <div className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {hotel.phone}
                    </div>
                  )}
                  {hotel.email && (
                    <div className="flex items-center">
                      <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {hotel.email}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t flex justify-end space-x-2">
                  <button
                    onClick={() => handleEdit(hotel)}
                    className="btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleViewStats(hotel)}
                    className="btn-secondary btn-sm"
                  >
                    View Stats
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500">
            No hotels found
          </div>
        )}
      </div>

      {/* Edit/Add Hotel Modal */}
      {(showEditModal || showAddModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModals}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedHotel ? 'Edit Hotel' : 'Add New Hotel'}
                </h3>
              </div>

              <form onSubmit={handleSave}>
                <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="label">Hotel Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Code</label>
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        className="input"
                        placeholder="e.g., SG-HQ"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Timezone</label>
                      <select
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        className="select"
                      >
                        <option value="Asia/Singapore">Asia/Singapore</option>
                        <option value="Asia/Manila">Asia/Manila</option>
                        <option value="Asia/Bangkok">Asia/Bangkok</option>
                        <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</option>
                        <option value="Asia/Jakarta">Asia/Jakarta</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                        <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="label">Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="input"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <button type="button" onClick={closeModals} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? (
                      <>
                        <div className="spinner spinner-sm mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Hotel'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && selectedHotel && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModals}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedHotel.name}</h3>
                  <p className="text-sm text-gray-500">Hotel Statistics</p>
                </div>
                <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-6">
                {statsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="spinner h-8 w-8"></div>
                  </div>
                ) : hotelStats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-blue-700">{hotelStats.totalTickets || 0}</div>
                      <div className="text-sm text-blue-600">Total Tickets</div>
                    </div>
                    <div className="bg-yellow-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-700">{hotelStats.openTickets || 0}</div>
                      <div className="text-sm text-yellow-600">Open Tickets</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-green-700">{hotelStats.resolvedTickets || 0}</div>
                      <div className="text-sm text-green-600">Resolved</div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-700">{hotelStats.totalUsers || 0}</div>
                      <div className="text-sm text-purple-600">Users</div>
                    </div>

                    <div className="col-span-2 md:col-span-4 mt-4">
                      <h4 className="font-medium text-gray-900 mb-3">Additional Details</h4>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Code:</span>
                          <span className="font-medium">{selectedHotel.code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Location:</span>
                          <span className="font-medium">{selectedHotel.city}, {selectedHotel.country}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Timezone:</span>
                          <span className="font-medium">{selectedHotel.timezone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className={`badge ${selectedHotel.is_active ? 'badge-green' : 'badge-red'}`}>
                            {selectedHotel.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {hotelStats.avgResolutionTime && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg Resolution Time:</span>
                            <span className="font-medium">{hotelStats.avgResolutionTime}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Unable to load statistics</p>
                    <p className="text-sm mt-1">Stats endpoint may not be available</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button onClick={closeModals} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelList;
